import React, { useMemo } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Nav from "@/components/Nav";
import { useRegisterForm } from "@/hooks/useRegisterForm";
import { getPendingUpload, getPendingUploadName, clearPendingUpload } from "@/lib/pendingUpload";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileCheck2 } from "lucide-react";

export default function Register() {
  const { user } = useAuth();
  const nav = useNavigate();
  // iter-62 — read the hero-dropped file (if any) once at mount so a
  // clear-on-submit doesn't nuke the banner text mid-render.
  const pendingName = useMemo(() => getPendingUploadName(), []);

  // After a successful register we optionally auto-continue into an
  // upload → book editor. Keeping this out of the useRegisterForm hook
  // so the hook stays reusable elsewhere.
  const handleSuccess = async () => {
    const file = getPendingUpload();
    if (!file) {
      nav("/dashboard");
      return;
    }
    // Guess a title from the filename; the parser will overwrite it
    // after reading the manuscript metadata anyway.
    const title = file.name.replace(/\.(docx|pdf|md|txt)$/i, "") || "Untitled";
    try {
      const { data: book } = await api.post("/books", { title, author: "" });
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/books/${book.id}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      clearPendingUpload();
      toast.success("Manuscript uploaded — preparing your preview");
      nav(`/book/${book.id}`);
    } catch (err) {
      // Any failure here shouldn't strand the new user — send them to
      // the dashboard and surface the error there.
      toast.error(`Upload failed: ${formatApiError(err)}. You can retry from the library.`);
      clearPendingUpload();
      nav("/dashboard");
    }
  };

  const {
    name, setName, email, setEmail, password, setPassword,
    refCode, busy, submit,
  } = useRegisterForm(handleSuccess);

  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen">
      <Nav />
      <div className="max-w-[1400px] mx-auto px-6 py-16 grid grid-cols-12 gap-12">
        <div className="col-span-12 lg:col-span-6 hidden lg:block">
          <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">Begin a new title</p>
          <h1 className="font-editorial text-7xl leading-[0.95] mt-4">Every book<br/>starts here.</h1>
          <p className="mt-6 text-[color:var(--text-muted)] max-w-md">
            An account lets you save drafts, generate covers, and re-export whenever you make edits.
          </p>
        </div>
        <div className="col-span-12 lg:col-span-6">
          <div className="max-w-md ml-auto bg-white border border-[color:var(--border)] p-10">
            <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)] mb-2">§ Create account</p>
            <h2 className="font-editorial text-3xl mb-4">Start your library</h2>
            {pendingName && (
              <div
                className="mb-6 flex items-start gap-3 border border-[color:var(--primary)] bg-[#fff7f2] px-4 py-3 rounded"
                data-testid="register-pending-upload"
              >
                <FileCheck2 className="w-5 h-5 mt-0.5 text-[color:var(--primary)] shrink-0" strokeWidth={1.5} />
                <div className="text-sm">
                  <div className="font-medium">Continuing with your file</div>
                  <div className="text-[color:var(--text-muted)] break-all">
                    {pendingName} · we'll upload it right after you finish signup
                  </div>
                </div>
              </div>
            )}
            <form onSubmit={submit} className="space-y-5" data-testid="register-form">
              {refCode && (
                <div className="text-xs uppercase tracking-widest px-3 py-2 border border-[color:var(--border)] bg-[color:var(--surface)]/40"
                     data-testid="register-ref-banner">
                  Referral applied · <span className="font-mono">{refCode}</span>
                </div>
              )}
              <TextField label="Your name" value={name} onChange={setName} testId="register-name" />
              <TextField label="Email" type="email" value={email} onChange={setEmail} testId="register-email" />
              <TextField label="Password (min 6)" type="password" value={password} onChange={setPassword} testId="register-password" />
              <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="register-submit">
                {busy ? "Creating…" : pendingName ? "Create account & upload" : "Create account"}
              </button>
              <p className="text-sm text-[color:var(--text-muted)]">
                Have an account? <Link to="/login" className="link-underline text-[color:var(--text)]" data-testid="register-login-link">Sign in</Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest mb-1">{label}</span>
      <input type={type} required value={value} onChange={(e)=>onChange(e.target.value)}
        className="w-full border border-[color:var(--border)] px-3 py-2 focus:outline-none focus:border-[color:var(--primary)]"
        data-testid={testId}/>
    </label>
  );
}
